import { type ElementType } from "react";
import { cn } from "@/lib/utils";

/**
 * Loading placeholder with a brand sheen sweep instead of a flat pulse —
 * reads as "data is on its way" rather than "this is broken". Falls back to
 * a static muted block under prefers-reduced-motion (the shimmer layer is
 * purely decorative).
 */
function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md bg-coral/10 ring-1 ring-coral/5",
        className,
      )}
      {...props}
    >
      <div
        aria-hidden
        className="absolute inset-0 -translate-x-full bg-gradient-sheen animate-shimmer motion-reduce:hidden"
      />
    </div>
  );
}

interface SkeletonListProps {
  /** Number of placeholder rows. Default 3. */
  rows?: number;
  /** Height class applied to each row's `Skeleton` (e.g. `"h-10"`, `"h-20"`). */
  rowClassName?: string;
  /** Wrapper element. Default `div`; pass `"ul"` for a semantic list. */
  as?: ElementType;
  /** Per-row element. Default `div`; pass `"li"` when `as="ul"`. */
  itemAs?: ElementType;
  className?: string;
}

/**
 * The "N stacked `Skeleton` rows while a list/table loads" shape repeated
 * ad hoc across Relay screens (bookings, forms, referrals, inbox, …) —
 * named and parameterized instead of each screen re-declaring its own
 * `ListSkeleton`/`Array.from({length: N})` loop.
 */
function SkeletonList({
  rows = 3,
  rowClassName = "h-10 w-full",
  as: Wrapper = "div",
  itemAs: Item = "div",
  className,
}: SkeletonListProps) {
  return (
    <Wrapper className={cn("space-y-2", className)}>
      {Array.from({ length: rows }).map((_, idx) => (
        <Item key={idx}>
          <Skeleton className={rowClassName} />
        </Item>
      ))}
    </Wrapper>
  );
}

export { Skeleton, SkeletonList };
